import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './Nocautes.css';

/**
 * Página de Nocautes - Ranking e Trocas
 */

const Nocautes = () => {
  const { user } = useAuth();
  const [rankingDados, setRankingDados] = useState([]);
  const [rankingVitimas, setRankingVitimas] = useState([]);
  const [meusNocautes, setMeusNocautes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tipoRanking, setTipoRanking] = useState('geral'); // 'geral' ou 'rodada'
  const [rodadaSelecionada, setRodadaSelecionada] = useState('');
  const [rodadas, setRodadas] = useState([]);
  const [fraseNocautes, setFraseNocautes] = useState('🥊 Ranking de Nocautes');

  useEffect(() => {
    carregarRodadas();
    carregarDados();
    carregarTemaAtivo();
  }, []);

  const carregarTemaAtivo = async () => {
    try {
      const response = await apiClient.get('/temas/ativo');
      if (response.data?.frase_nocautes) {
        setFraseNocautes(response.data.frase_nocautes);
      }
    } catch (err) {
      console.error('Erro ao carregar tema:', err);
    }
  };

  useEffect(() => {
    if (tipoRanking === 'geral') {
      carregarDados();
    } else if (tipoRanking === 'rodada' && rodadaSelecionada) {
      carregarDadosPorRodada(rodadaSelecionada);
    } else if (tipoRanking === 'rodada' && !rodadaSelecionada) {
      // Limpar dados quando não há rodada selecionada
      setRankingDados([]);
      setRankingVitimas([]);
      setLoading(false);
    }
  }, [tipoRanking, rodadaSelecionada]);

  const carregarRodadas = async () => {
    try {
      const response = await apiClient.get('/rodadas');
      // Filtrar apenas rodadas fechadas e ordenar por data (mais recente primeiro)
      const rodadasFechadas = (response.data || [])
        .filter(r => r.status === 'fechada')
        .sort((a, b) => new Date(b.data) - new Date(a.data));
      setRodadas(rodadasFechadas);
      
      // Se não há rodada selecionada e há rodadas, selecionar a primeira
      if (!rodadaSelecionada && rodadasFechadas.length > 0) {
        setRodadaSelecionada(rodadasFechadas[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar rodadas:', err);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [rankingDadosRes, rankingVitimasRes, nocautesRes] = await Promise.all([
        apiClient.get('/nocautes/ranking-dados'),
        apiClient.get('/nocautes/ranking-vitimas'),
        apiClient.get(`/nocautes/jogador/${user.id}`),
      ]);

      setRankingDados(rankingDadosRes.data);
      setRankingVitimas(rankingVitimasRes.data);
      setMeusNocautes(nocautesRes.data);

      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const carregarDadosPorRodada = async (rodadaId) => {
    try {
      setLoading(true);
      const [rankingDadosRes, rankingVitimasRes, nocautesRes] = await Promise.all([
        apiClient.get(`/nocautes/ranking-dados/rodada/${rodadaId}`),
        apiClient.get(`/nocautes/ranking-vitimas/rodada/${rodadaId}`),
        apiClient.get(`/nocautes/jogador/${user.id}`),
      ]);

      setRankingDados(rankingDadosRes.data);
      setRankingVitimas(rankingVitimasRes.data);
      setMeusNocautes(nocautesRes.data);

      setError('');
    } catch (err) {
      console.error('Erro ao carregar dados da rodada:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados da rodada');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  const nocautesDisponiveis = meusNocautes?.nocautes_dados?.disponiveis || 0;

  return (
    <div className="container nocautes-page">
      <div className="page-header tolkien-nocautes-theme">
        <div className="theme-header-fight">
          <span className="tolkien-fight-icon">⚔️</span>
          <h1>{fraseNocautes}</h1>
          <span className="tolkien-fight-icon">⚔️</span>
        </div>
        <p className="theme-subtitle-fight">"No RPG, cada dado lançado é uma porta para o inesperado!!!"</p>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Seletor de Tipo de Ranking */}
      <div className="nocautes-selector">
        <div className="selector-group">
          <label>
            <input
              type="radio"
              name="tipoRankingNocautes"
              value="geral"
              checked={tipoRanking === 'geral'}
              onChange={(e) => setTipoRanking(e.target.value)}
            />
            <span>Ranking Geral</span>
          </label>
          <label>
            <input
              type="radio"
              name="tipoRankingNocautes"
              value="rodada"
              checked={tipoRanking === 'rodada'}
              onChange={(e) => setTipoRanking(e.target.value)}
            />
            <span>Ranking por Rodada</span>
          </label>
        </div>

        {tipoRanking === 'rodada' && (
          <div className="rodada-selector">
            <label htmlFor="rodada-select-nocautes">Selecione a Rodada:</label>
            <select
              id="rodada-select-nocautes"
              value={rodadaSelecionada}
              onChange={(e) => setRodadaSelecionada(e.target.value)}
            >
              <option value="">Selecione uma rodada...</option>
              {rodadas.map(rodada => (
                <option key={rodada.id} value={rodada.id}>
                  {new Date(rodada.data).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {meusNocautes && (
        <div className="meus-nocautes-card">
          <h2>Meus Nocautes</h2>
          <div className="nocautes-stats">
            <div className="stat-item">
              <span className="stat-label">Total de Nocautes Dados:</span>
              <span className="stat-value">{meusNocautes.nocautes_dados.total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Disponíveis para Troca:</span>
              <span className="stat-value disponivel">{nocautesDisponiveis}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Já Utilizados em Trocas:</span>
              <span className="stat-value">{meusNocautes.nocautes_dados.usados_em_trocas || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Nocautes Recebidos:</span>
              <span className="stat-value">{meusNocautes.nocautes_recebidos.total}</span>
            </div>
          </div>
        </div>
      )}

      <div className="rankings-grid">
            <div className="ranking-section">
              <h2>🏆 Ranking de Nocautes Dados</h2>
              {tipoRanking === 'rodada' && !rodadaSelecionada ? (
                <p className="no-data">Selecione uma rodada para ver o ranking.</p>
              ) : rankingDados.length === 0 ? (
                <p className="no-data">
                  {tipoRanking === 'geral' 
                    ? 'Nenhum nocaute registrado ainda.'
                    : 'Nenhum nocaute registrado nesta rodada.'}
                </p>
              ) : (
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th>Pos</th>
                      <th>Jogador</th>
                      <th>Total</th>
                      <th>Disponíveis</th>
                      <th>Usados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingDados.map((item, index) => (
                      <tr key={item.jogador.id} className={index < 3 ? `top-${index + 1}` : ''}>
                        <td>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && `${item.posicao}º`}
                        </td>
                        <td>{item.jogador.nome}</td>
                        <td><strong>{item.total_nocautes}</strong></td>
                        <td className="disponivel">{item.nocautes_disponiveis}</td>
                        <td>{item.nocautes_usados}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="ranking-section">
              <h2>💀 Ranking de Vítimas (Mais Nocauteados)</h2>
              {tipoRanking === 'rodada' && !rodadaSelecionada ? (
                <p className="no-data">Selecione uma rodada para ver o ranking.</p>
              ) : rankingVitimas.length === 0 ? (
                <p className="no-data">
                  {tipoRanking === 'geral' 
                    ? 'Nenhuma vítima registrada ainda.'
                    : 'Nenhuma vítima registrada nesta rodada.'}
                </p>
              ) : (
                <table className="ranking-table">
                  <thead>
                    <tr>
                      <th>Pos</th>
                      <th>Jogador</th>
                      <th>Nocautes Recebidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingVitimas.map((item, index) => (
                      <tr key={item.jogador.id} className={index < 3 ? `top-${index + 1}` : ''}>
                        <td>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && `${item.posicao}º`}
                        </td>
                        <td>{item.jogador.nome}</td>
                        <td><strong>{item.total_nocautes_recebidos}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
    </div>
  );
};

export default Nocautes;
